import React, { useState } from "react";
import { useNavigation } from "react-navi";
import { PostData } from "../Service/service";

export default function LoginPage({ currentUser, authService }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const navigation = useNavigation();

  function onSubmit(e) {
    e.preventDefault();

    if (!username || !password) {
      setError("Please enter your username and password.");
      return;
    }

    setError("");
    setIsLoading(true);

    PostData("/login", { email: username, password })
      .then((resp) => {
        authService.setTokenAfterLogin(resp).then((ok) => {
          setIsLoading(false);
          if (ok === true) {
            navigation.navigate("/mis").then(() => window.location.reload());
          } else {
            setError(typeof ok === "string" ? ok : "Invalid credentials. Please try again.");
          }
        });
      })
      .catch(() => {
        setIsLoading(false);
        setError("We couldn't sign you in. Please check your connection and try again.");
      });
  }

  return (
    <div className="min-h-screen w-screen flex bg-surface">
      {/* Brand panel — hidden on mobile */}
      <aside className="hidden lg:flex relative w-1/2 flex-col justify-between p-12 overflow-hidden text-white">
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(135deg, #0f766e 0%, #115e59 45%, #134e4a 100%)",
          }}
        />
        <div
          className="absolute inset-0 opacity-40"
          style={{
            backgroundImage:
              "radial-gradient(circle at 20% 20%, rgba(45,212,191,0.35), transparent 45%), radial-gradient(circle at 80% 90%, rgba(20,184,166,0.25), transparent 50%)",
          }}
        />

        <div className="relative flex items-center gap-3">
          <img
            src="/logo512.png"
            alt="Shishu Sarothi"
            className="w-10 h-10 rounded-lg bg-white/10 p-1"
          />
          <div className="leading-tight">
            <div className="text-xs uppercase tracking-widest text-brand-100/80">
              Shishu Sarothi
            </div>
            <div className="text-base font-semibold">MIS Panel</div>
          </div>
        </div>

        <div className="relative max-w-md">
          <h2 className="text-4xl font-semibold leading-tight text-white">
            Welcome back.
          </h2>
          <p className="mt-4 text-base text-brand-50/80 leading-relaxed">
            Manage projects, indents, invoices and activities — all from a
            single, focused workspace.
          </p>
        </div>

        <div className="relative text-xs text-brand-100/70">
          &copy; {new Date().getFullYear()} Shishu Sarothi. All rights reserved.
        </div>
      </aside>

      {/* Form panel */}
      <main className="flex-1 flex items-center justify-center p-6 sm:p-10">
        <div className="w-full max-w-sm">
          {/* Mobile brand mark */}
          <div className="lg:hidden flex items-center gap-3 mb-10">
            <img
              src="/logo512.png"
              alt="Shishu Sarothi"
              className="w-9 h-9 rounded-lg"
            />
            <div className="leading-tight">
              <div className="text-xxs uppercase tracking-widest text-ink-500">
                Shishu Sarothi
              </div>
              <div className="text-sm font-semibold text-ink-900">MIS Panel</div>
            </div>
          </div>

          <div className="mb-8">
            <h1 className="text-2xl font-semibold text-ink-900">Sign in</h1>
            <p className="mt-1 text-sm text-ink-500">
              Enter your credentials to access your dashboard.
            </p>
          </div>

          {error && (
            <div
              role="alert"
              className="mb-5 flex items-start gap-2 px-3 py-2.5 rounded-md bg-red-50 border border-red-100 text-sm text-red-700"
            >
              <svg
                className="w-4 h-4 mt-0.5 flex-shrink-0"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M18 10A8 8 0 11 2 10a8 8 0 0116 0zm-7-4a1 1 0 10-2 0v4a1 1 0 102 0V6zm-1 8a1 1 0 100-2 1 1 0 000 2z"
                  clipRule="evenodd"
                />
              </svg>
              <span>{error}</span>
            </div>
          )}

          <form onSubmit={onSubmit} className="space-y-4" noValidate>
            <div>
              <label
                htmlFor="username"
                className="block text-xs font-medium text-ink-700 mb-1.5"
              >
                Email or username
              </label>
              <input
                id="username"
                name="username"
                type="text"
                autoComplete="username"
                className="ss-input"
                placeholder="you@shishusarothi.org"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                disabled={isLoading}
                aria-invalid={!!error}
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label
                  htmlFor="password"
                  className="block text-xs font-medium text-ink-700"
                >
                  Password
                </label>
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="text-xs text-ink-500 hover:text-ink-700 transition-colors"
                  tabIndex={-1}
                >
                  {showPassword ? "Hide" : "Show"}
                </button>
              </div>
              <input
                id="password"
                name="password"
                type={showPassword ? "text" : "password"}
                autoComplete="current-password"
                className="ss-input"
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={isLoading}
                aria-invalid={!!error}
              />
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="ss-btn-primary w-full py-2.5 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <>
                  <svg
                    className="w-4 h-4 animate-spin"
                    viewBox="0 0 24 24"
                    fill="none"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                    />
                  </svg>
                  Signing in…
                </>
              ) : (
                "Sign in"
              )}
            </button>
          </form>

          <div className="mt-6 text-center">
            <button
              type="button"
              onClick={() => navigation.navigate(`/forgot-password`)}
              className="text-sm text-brand-700 hover:text-brand-600 font-medium transition-colors"
            >
              Forgot password?
            </button>
          </div>

          <div className="lg:hidden mt-10 text-center text-xxs text-ink-400">
            &copy; {new Date().getFullYear()} Shishu Sarothi
          </div>
        </div>
      </main>
    </div>
  );
}
